import {
  MinusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Select,
  Switch,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  getRenewalDateRangeError,
  getSubOfficeOptions,
  shouldReactivateExpiredSubscription,
} from "./prismSubscriptionUtils";
import "../../styles/pages/prism/prismSubscriptionForm.scss";

const FREQUENCY_OPTIONS = [
  { value: "annual", label: "Annual" },
  { value: "semi_annual", label: "Semi-annual" },
  { value: "quarterly", label: "Quarterly" },
  { value: "monthly", label: "Monthly" },
];

export const ACCOUNT_HOLDER_NAME_RULES = [
  {
    required: true,
    whitespace: true,
    message: "Please enter the account holder name.",
  },
];

const SUBSCRIPTION_FIELD_HELP = Object.freeze({
  subscriptionName:
    "Enter the recognizable service or product name used in PRISM lists, reports, and calendar events.",
  description:
    "Optionally describe the subscription's purpose, coverage, or services included.",
  department:
    "Select the office responsible for this subscription.",
  subDepartment:
    "Select the exact unit responsible for the subscription, or leave it blank for an office-level subscription.",
  subscriberEmail:
    "The primary recipient is the employee who created the subscription and cannot be changed.",
  subscriptionType:
    "Classify the subscription as Free, Premium, or Institutional for reporting.",
  status:
    "Set the subscription's current lifecycle state: Active, Expired, or Cancelled.",
  renewalFrequency:
    "Select how often the subscription is normally renewed, or leave it blank when not applicable.",
  autoRenewal:
    "Enable this when the provider renews the subscription automatically without a manual purchase.",
  contractStartDate:
    "Select the date the subscription contract or service period begins.",
  contractEndDate:
    "Select the final date of the current contract or service period.",
  renewalDate:
    "Select the planned renewal date within the contract start and end dates.",
  billingCycle:
    "Select how often the provider normally bills for this subscription.",
  paymentMethod:
    "Select the payment method normally used for this subscription.",
  accountHolder:
    "Enter the person, account, or organization the subscription is paid to.",
  bankName:
    "Enter the bank the payment is transferred through.",
  addPaymentAfterCreate:
    "After the subscription is created successfully, open the Add Payment step. Cancelling that step does not undo the subscription.",
  addPaymentAfterCreateUnavailable:
    "An exact unit grant is required to add a payment after creating this unit subscription.",
});

export const CREDIT_CARD_PAYEE_NAME = "Chancellor (BDO)";

const toDateString = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.format("YYYY-MM-DD");
  return dayjs(value).format("YYYY-MM-DD");
};

const normalizeOptionalText = (value) => {
  if (typeof value !== "string") return value ?? null;

  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

const PrismSubscriptionForm = ({
  form,
  officeOptions,
  loading,
  onCancel,
  onSubmit,
  mode = "add",
  originalSubscription = null,
  fixedOffice = null,
  fixedUnit = null,
  officeLocked = false,
  unitLocked = false,
  subscriptionDisabled = false,
  getAllowedUnitOptions = getSubOfficeOptions,
  canUseOfficeLevelScope = () => true,
  canAddPaymentAfterCreateForScope = () => true,
}) => {
  const contractStartDate = Form.useWatch("contract_start_date", form);
  const contractEndDate = Form.useWatch("contract_end_date", form);
  const autoRenewalEnabled = Form.useWatch("auto_renewal_status", form);
  const selectedDepartment = Form.useWatch("subscriber_department", form);
  const selectedSubDepartment = Form.useWatch("subscriber_sub_department", form);
  const addPaymentAfterCreate = Form.useWatch(
    "add_payment_after_create",
    form,
  );
  const paymentMethod = Form.useWatch("payment_method", form);
  const isCreditCardPayment = paymentMethod === "credit_card";
  const isBankTransferPayment = paymentMethod === "bank_transfer";
  const subOfficeOptions = getAllowedUnitOptions(
    selectedDepartment,
    selectedSubDepartment,
  );
  const hasSubOffices =
    subOfficeOptions.length > 0 || Boolean(selectedSubDepartment);
  const officeLevelScopeAllowed = canUseOfficeLevelScope(selectedDepartment);
  const unitRequired = Boolean(selectedDepartment) && !officeLevelScopeAllowed;
  const sectionIdPrefix = `prism-subscription-${mode}`;
  const destinationOffice = officeLocked ? fixedOffice : selectedDepartment;
  const destinationUnit = unitLocked ? fixedUnit : selectedSubDepartment;
  const paymentAfterCreateAllowed = canAddPaymentAfterCreateForScope(
    destinationOffice,
    destinationUnit,
  );

  const shouldReactivateForEndDate = (nextContractEndDate) =>
    mode === "edit" &&
    shouldReactivateExpiredSubscription(
      originalSubscription?.contract_status,
      originalSubscription?.contract_end_date,
      nextContractEndDate,
    );

  const handleContractEndDateChange = (nextContractEndDate) => {
    if (
      form.getFieldValue("contract_status") === "expired" &&
      shouldReactivateForEndDate(nextContractEndDate)
    ) {
      form.setFieldValue("contract_status", "active");
    }

    if (form.getFieldValue("contract_renewal_date")) {
      form.validateFields(["contract_renewal_date"]).catch(() => {
        // Keep the existing value visible; Form.Item displays the range error.
      });
    }
  };
  const expiredStatusDisabled = shouldReactivateForEndDate(contractEndDate);

  const handleFinish = (values) => {
    const {
      add_payment_after_create: addPaymentAfterCreateValue,
      ...subscriptionValues
    } = values;
    delete subscriptionValues.subscriber_email;
    const contractStatus =
      subscriptionValues.contract_status === "expired" &&
      shouldReactivateForEndDate(subscriptionValues.contract_end_date)
        ? "active"
        : subscriptionValues.contract_status;
    const payload = {
      ...subscriptionValues,
      subscription_name: subscriptionValues.subscription_name.trim(),
      subscription_details: normalizeOptionalText(
        subscriptionValues.subscription_details,
      ),
      subscriber_department: officeLocked
        ? fixedOffice
        : subscriptionValues.subscriber_department,
      subscriber_sub_department: unitLocked
        ? fixedUnit
        : hasSubOffices
          ? normalizeOptionalText(subscriptionValues.subscriber_sub_department)
          : null,
      subscriber_additional_emails:
        subscriptionValues.subscriber_additional_emails || [],
      account_holder_name: isCreditCardPayment
        ? CREDIT_CARD_PAYEE_NAME
        : subscriptionValues.account_holder_name.trim(),
      bank_name: isBankTransferPayment
        ? (subscriptionValues.bank_name || "").trim()
        : null,
      contract_renewal_frequency:
        subscriptionValues.contract_renewal_frequency ?? null,
      auto_renewal_status: Boolean(subscriptionValues.auto_renewal_status),
      contract_status: contractStatus,
      contract_start_date: toDateString(subscriptionValues.contract_start_date),
      contract_end_date: toDateString(subscriptionValues.contract_end_date),
      contract_renewal_date: toDateString(
        subscriptionValues.contract_renewal_date,
      ),
      billing_cycle: subscriptionValues.billing_cycle ?? null,
      payment_method: subscriptionValues.payment_method ?? null,
    };

    onSubmit(payload, {
      addPaymentAfterCreate:
        mode === "add" &&
        paymentAfterCreateAllowed &&
        Boolean(addPaymentAfterCreateValue),
    });
  };

  return (
    <Form
      className="prism-subscription-form"
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{
        subscription_type: "premium",
        contract_status: "active",
        contract_renewal_frequency: "annual",
        auto_renewal_status: false,
        billing_cycle: "annual",
        payment_method: "other",
        add_payment_after_create: false,
        subscriber_additional_emails: [],
      }}
    >
      <section
        aria-labelledby={`${sectionIdPrefix}-basic-heading`}
        className="prism-subscription-form__section"
      >
        <div className="prism-subscription-form__section-heading">
          <h3 id={`${sectionIdPrefix}-basic-heading`}>Basic Information</h3>
          <p>Identify the subscription and its primary contact.</p>
        </div>
        <div className="prism-subscription-form__section-grid prism-subscription-form__section-grid--basic">
          <Form.Item
            name="subscription_name"
            label="Subscription Name"
            tooltip={SUBSCRIPTION_FIELD_HELP.subscriptionName}
            rules={[
              { required: true, message: "Please enter a subscription name." },
              {
                validator: (_, value) =>
                  typeof value === "string" && value.trim()
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Please enter a subscription name."),
                      ),
              },
            ]}
          >
            <Input placeholder="e.g. Microsoft 365" />
          </Form.Item>

          <Form.Item
            name="subscription_type"
            label="Subscription Type"
            tooltip={SUBSCRIPTION_FIELD_HELP.subscriptionType}
            rules={[
              { required: true, message: "Please select a subscription type." },
            ]}
          >
            <Select
              placeholder="Select subscription type"
              options={[
                { value: "free", label: "Free" },
                { value: "premium", label: "Premium" },
                { value: "institutional", label: "Institutional" },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="subscription_details"
            label="Description"
            tooltip={SUBSCRIPTION_FIELD_HELP.description}
          >
            <Input.TextArea
              placeholder="Briefly describe the service or purpose"
              rows={1}
            />
          </Form.Item>

          <Form.Item
            name="subscriber_email"
            label="Subscriber Email"
            tooltip={SUBSCRIPTION_FIELD_HELP.subscriberEmail}
          >
            <Input disabled placeholder="Creator email" />
          </Form.Item>

          <Form.List name="subscriber_additional_emails">
            {(fields, { add, remove }) => (
              <div className="prism-subscription-form__additional-emails">
                <div className="prism-subscription-form__additional-emails-header">
                  <span>Additional Subscriber Emails</span>
                  {fields.length < 2 && (
                    <Button icon={<PlusOutlined />} onClick={() => add()} size="small">
                      Add Email
                    </Button>
                  )}
                </div>
                {fields.map((field, index) => (
                  <Form.Item
                    {...field}
                    key={field.key}
                    label={`Additional Email ${index + 1}`}
                    rules={[
                      { required: true, message: "Please enter an email address." },
                      { type: "email", message: "Please enter a valid email address." },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value) return Promise.resolve();
                          const normalized = String(value).trim().toLowerCase();
                          const primary = String(
                            getFieldValue("subscriber_email") || "",
                          )
                            .trim()
                            .toLowerCase();
                          const extras = (
                            getFieldValue("subscriber_additional_emails") || []
                          ).map((email) => String(email || "").trim().toLowerCase());

                          if (
                            normalized === primary ||
                            extras.filter((email) => email === normalized).length > 1
                          ) {
                            return Promise.reject(
                              new Error("Subscriber email addresses must be unique."),
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input
                      placeholder="additional@upou.edu.ph"
                      suffix={
                        <MinusCircleOutlined
                          aria-label={`Remove additional email ${index + 1}`}
                          onClick={() => remove(field.name)}
                        />
                      }
                    />
                  </Form.Item>
                ))}
              </div>
            )}
          </Form.List>
        </div>
      </section>

      <section
          aria-labelledby={`${sectionIdPrefix}-assignment-heading`}
          className="prism-subscription-form__section"
        >
          <div className="prism-subscription-form__section-heading">
            <h3 id={`${sectionIdPrefix}-assignment-heading`}>
              Office/Unit Assignment
            </h3>
            <p>Choose the exact office or unit responsible for this subscription.</p>
          </div>
          <div className="prism-subscription-form__section-grid prism-subscription-form__section-grid--assignment">
            <Form.Item
              name="subscriber_department"
              label="Office"
              tooltip={SUBSCRIPTION_FIELD_HELP.department}
              rules={[
                {
                  required: true,
                  message: "Please select an office.",
                },
              ]}
            >
              <Select
                disabled={officeLocked}
                showSearch
                placeholder="Select office"
                options={officeOptions}
                onChange={() =>
                  form.setFieldValue("subscriber_sub_department", null)
                }
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item
              name="subscriber_sub_department"
              label="Unit"
              tooltip={SUBSCRIPTION_FIELD_HELP.subDepartment}
              rules={[
                {
                  required: unitRequired,
                  message: "Please select a unit for your assigned scope.",
                },
              ]}
            >
              <Select
                allowClear={hasSubOffices && officeLevelScopeAllowed}
                disabled={unitLocked || !hasSubOffices}
                placeholder={
                  officeLevelScopeAllowed ? "Office-level (no unit)" : "Select unit"
                }
                options={subOfficeOptions}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>
        </section>

      <section
        aria-labelledby={`${sectionIdPrefix}-contract-heading`}
        className="prism-subscription-form__section"
      >
        <div className="prism-subscription-form__section-heading">
          <h3 id={`${sectionIdPrefix}-contract-heading`}>Contract Details</h3>
          <p>Set the lifecycle, renewal schedule, and contract dates.</p>
        </div>
        <div className="prism-subscription-form__section-grid prism-subscription-form__section-grid--contract">
          {subscriptionDisabled ? (
            <>
              <Form.Item name="contract_status" hidden>
                <Input />
              </Form.Item>
              <Form.Item label="Status" tooltip={SUBSCRIPTION_FIELD_HELP.status}>
                <Input disabled value="Disabled" />
              </Form.Item>
            </>
          ) : (
            <Form.Item
              name="contract_status"
              label="Status"
              tooltip={SUBSCRIPTION_FIELD_HELP.status}
              rules={[{ required: true, message: "Please select a status." }]}
            >
              <Select
                placeholder="Select contract status"
                options={[
                  { value: "active", label: "Active" },
                  {
                    value: "expired",
                    label: "Expired",
                    disabled: expiredStatusDisabled,
                  },
                  { value: "cancelled", label: "Cancelled" },
                ]}
              />
            </Form.Item>
          )}

          <Form.Item
            name="contract_renewal_frequency"
            label="Renewal Frequency"
            tooltip={SUBSCRIPTION_FIELD_HELP.renewalFrequency}
          >
            <Select
              allowClear
              placeholder="Select renewal frequency"
              options={FREQUENCY_OPTIONS}
            />
          </Form.Item>

          <Form.Item
            label="Auto-renewal"
            tooltip={SUBSCRIPTION_FIELD_HELP.autoRenewal}
          >
            <div className="prism-subscription-form__switch-field">
              <Form.Item
                name="auto_renewal_status"
                noStyle
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <span>{autoRenewalEnabled ? "Enabled" : "Disabled"}</span>
            </div>
          </Form.Item>

          <Form.Item
            name="contract_start_date"
            label="Contract Start Date"
            tooltip={SUBSCRIPTION_FIELD_HELP.contractStartDate}
            rules={[{ required: true, message: "Please select a start date." }]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              placeholder="Select start date"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            name="contract_end_date"
            label="Contract End Date"
            tooltip={SUBSCRIPTION_FIELD_HELP.contractEndDate}
            dependencies={["contract_start_date"]}
            rules={[
              { required: true, message: "Please select an end date." },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue("contract_start_date");

                  if (
                    !value ||
                    !startDate ||
                    !dayjs(value).isBefore(startDate, "day")
                  ) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error("End date must be on or after start date."),
                  );
                },
              }),
            ]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              placeholder="Select end date"
              style={{ width: "100%" }}
              onChange={handleContractEndDateChange}
              disabledDate={(current) =>
                contractStartDate && current
                  ? current.isBefore(contractStartDate, "day")
                  : false
              }
            />
          </Form.Item>

          <Form.Item
            name="contract_renewal_date"
            label="Renewal Date"
            tooltip={SUBSCRIPTION_FIELD_HELP.renewalDate}
            dependencies={["contract_start_date", "contract_end_date"]}
            rules={[
              { required: true, message: "Please select a renewal date." },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const rangeError = getRenewalDateRangeError(
                    value,
                    getFieldValue("contract_start_date"),
                    getFieldValue("contract_end_date"),
                  );

                  if (!rangeError) {
                    return Promise.resolve();
                  }

                  return Promise.reject(new Error(rangeError));
                },
              }),
            ]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              placeholder="Select renewal date"
              style={{ width: "100%" }}
              disabledDate={(current) => {
                if (!current) return false;

                const isBeforeStart = contractStartDate
                  ? current.isBefore(contractStartDate, "day")
                  : false;
                const isAfterEnd = contractEndDate
                  ? current.isAfter(contractEndDate, "day")
                  : false;

                return isBeforeStart || isAfterEnd;
              }}
            />
          </Form.Item>
        </div>
      </section>

      <section
        aria-labelledby={`${sectionIdPrefix}-billing-heading`}
        className="prism-subscription-form__section"
      >
        <div className="prism-subscription-form__section-heading">
          <h3 id={`${sectionIdPrefix}-billing-heading`}>
            Billing &amp; Account
          </h3>
          <p>Record the usual billing arrangement and account holder.</p>
        </div>
        <div className="prism-subscription-form__section-grid prism-subscription-form__section-grid--billing">
          <Form.Item
            name="billing_cycle"
            label="Billing Cycle"
            tooltip={SUBSCRIPTION_FIELD_HELP.billingCycle}
          >
            <Select
              allowClear
              placeholder="Select billing cycle"
              options={FREQUENCY_OPTIONS}
            />
          </Form.Item>

          <Form.Item
            name="payment_method"
            label="Payment Method"
            tooltip={SUBSCRIPTION_FIELD_HELP.paymentMethod}
          >
            <Select
              allowClear
              placeholder="Select payment method"
              options={[
                { value: "credit_card", label: "Credit Card" },
                { value: "bank_transfer", label: "Bank Transfer" },
                { value: "paypal", label: "Paypal" },
                { value: "other", label: "Other" },
              ]}
              onChange={(value) => {
                if (value === "credit_card") {
                  form.setFieldValue(
                    "account_holder_name",
                    CREDIT_CARD_PAYEE_NAME,
                  );
                } else if (
                  form.getFieldValue("account_holder_name") ===
                  CREDIT_CARD_PAYEE_NAME
                ) {
                  form.setFieldValue("account_holder_name", "");
                }
                if (value !== "bank_transfer") {
                  form.setFieldValue("bank_name", undefined);
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="account_holder_name"
            label="Payee Name"
            tooltip={SUBSCRIPTION_FIELD_HELP.accountHolder}
            rules={ACCOUNT_HOLDER_NAME_RULES}
          >
            <Input
              placeholder="Person, account, or organization name"
              disabled={isCreditCardPayment}
            />
          </Form.Item>

          {isBankTransferPayment && (
            <Form.Item
              name="bank_name"
              label="Bank Name"
              tooltip={SUBSCRIPTION_FIELD_HELP.bankName}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please enter the bank name.",
                },
              ]}
            >
              <Input placeholder="e.g. BDO, BPI, Metrobank" />
            </Form.Item>
          )}
        </div>
      </section>

      <div
        className={`prism-subscription-form__footer ${
          mode === "edit" ? "prism-subscription-form__footer--actions-only" : ""
        }`}
      >
        {mode === "add" && (
          <Form.Item
            className="prism-subscription-form__payment-choice"
            name="add_payment_after_create"
            valuePropName="checked"
          >
            <Checkbox disabled={!paymentAfterCreateAllowed}>
              <span className="prism-subscription-form__payment-choice-label">
                Add payment after creating subscription
                <Tooltip
                  title={
                    paymentAfterCreateAllowed
                      ? SUBSCRIPTION_FIELD_HELP.addPaymentAfterCreate
                      : SUBSCRIPTION_FIELD_HELP.addPaymentAfterCreateUnavailable
                  }
                >
                  <QuestionCircleOutlined
                    aria-label="About adding a payment after creating the subscription"
                    className="prism-subscription-form__help-icon"
                    role="img"
                    tabIndex={0}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  />
                </Tooltip>
              </span>
            </Checkbox>
          </Form.Item>
        )}

        <Form.Item className="prism-subscription-form__actions">
          <div className="prism-subscription-form__action-buttons">
            <Button disabled={loading} onClick={onCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {mode === "edit"
                ? "Save Changes"
                : addPaymentAfterCreate
                  ? "Add Subscription & Continue to Payment"
                  : "Add Subscription"}
            </Button>
          </div>
        </Form.Item>
      </div>
    </Form>
  );
};

export default PrismSubscriptionForm;
